<template>
  <v-layout>
    <v-dialog :modelValue="dialog" @click:outside="$emit('updateDialog', false)" @keydown.esc="$emit('updateDialog', false)" max-width="900px" transition="dialog-bottom-transition">
      <template v-slot:activator="{ props }">
        <v-btn flat class="mb-3 btn-second" v-bind="props" @click="$emit('updateDialog', true)">{{ button_name }}</v-btn>
      </template>

      <v-card>
        <v-form @submit.prevent ref="form" v-model="valid" v-on:submit.prevent="createItem">
          <v-card-title>
            <span class="headline">{{ modal_title }}</span>
          </v-card-title>

          <v-card-text>
            <!-- START FORM DIALOG -->
            <v-row align="start" justify="center">
              <v-col md="10" class="mt-4 pr-0 pl-0">
                <div class="px-2 py-1 mb-3 bg-subtitle-section">
                  <p class="text-subtitle-1 mb-0"><b>Alcance de la política</b></p>
                </div>
                <p>La política calcula el mínimo, el máximo y las ubicaciones de picking de las reglas de reposición de los productos del rubro, según su demanda real.</p>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-combobox
                      label="Almacén*"
                      v-model="item.almacen"
                      :items="almacenes"
                      item-title="name"
                      item-value="id"
                      clearable
                      :disabled="disabled"
                      :rules="[rules.requiredComboBox]"
                      @update:modelValue="onAlmacenChange"
                    ></v-combobox>
                  </v-col>

                  <v-col cols="12" md="6" v-if="!isClient()">
                    <v-combobox
                      label="Cliente"
                      v-model="item.client"
                      :items="clients"
                      item-title="name"
                      item-value="id"
                      clearable
                      :disabled="disabled"
                      :rules="[rules.onlySelectFromList]"
                      hint="Vacío: aplica a los productos de todos los clientes"
                      persistent-hint
                    ></v-combobox>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-combobox
                      label="Rubro*"
                      v-model="item.category"
                      :items="categories"
                      item-title="name"
                      item-value="id"
                      clearable
                      :disabled="disabled"
                      :rules="[rules.requiredComboBox]"
                      @update:modelValue="onCategoryChange"
                    ></v-combobox>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-combobox
                      label="Subrubro"
                      v-model="item.subcategory"
                      :items="subcategories"
                      item-title="name"
                      item-value="id"
                      clearable
                      :disabled="disabled || item.category == null"
                      :rules="[rules.onlySelectFromList]"
                      hint="Vacío: aplica a todo el rubro"
                      persistent-hint
                    ></v-combobox>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-combobox
                      label="Tipo de ubicación de picking*"
                      v-model="item.tipo_ubicacion"
                      :items="tipos_ubicacion"
                      item-title="name"
                      item-value="id"
                      clearable
                      :disabled="disabled || item.almacen == null"
                      :loading="loading_tipos"
                      :rules="[rules.requiredComboBox]"
                      hint="Tipo de ubicación (de picking, de la sucursal del almacén) donde la política asigna ubicaciones"
                      persistent-hint
                    ></v-combobox>
                  </v-col>

                  <v-col cols="12" v-if="tipo_medidas_faltantes.length > 0">
                    <v-alert type="warning" density="compact" variant="tonal">
                      El tipo de ubicación <b>{{ item.tipo_ubicacion.name }}</b> no tiene cargado {{ tipo_medidas_faltantes.join(", ") }}: no se puede calcular la capacidad. Las reglas se van a crear
                      con una sola ubicación y el máximo no se va a topear. Cargá las medidas en Tipos de ubicación.
                    </v-alert>
                  </v-col>
                </v-row>

                <div class="px-2 py-1 mb-3 mt-6 bg-subtitle-section">
                  <p class="text-subtitle-1 mb-0"><b>Cálculo del mínimo y máximo</b></p>
                </div>

                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field label="Cobertura mín. (días)*" v-model="item.min_days" :disabled="disabled" type="number" min="0" step="0.5" :rules="[rules.required]">
                      <template v-slot:append-inner>
                        <v-tooltip location="top" max-width="350">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="small" color="grey">mdi-information-outline</v-icon>
                          </template>
                          <span>Días de demanda que cubre el stock mínimo. Mínimo = demanda diaria × días. Cuando el stock de la regla baja de ese valor se genera la reposición.</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field label="Cobertura máx. (días)*" v-model="item.max_days" :disabled="disabled" type="number" min="0" step="0.5" :rules="[rules.required]">
                      <template v-slot:append-inner>
                        <v-tooltip location="top" max-width="350">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="small" color="grey">mdi-information-outline</v-icon>
                          </template>
                          <span>Días de demanda que cubre el stock máximo (hasta donde se repone). Máximo = demanda diaria × días. Debe ser mayor a la cobertura del mínimo.</span>
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      label="Ventana de historia (días)"
                      v-model="item.back_period_days"
                      :disabled="disabled"
                      type="number"
                      min="1"
                      hint="Días hacia atrás de despachos para calcular la demanda diaria"
                      persistent-hint
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      label="Stock mínimo por defecto*"
                      v-model="item.stock_min_default"
                      :disabled="disabled"
                      type="number"
                      min="0"
                      :rules="[rules.required]"
                      hint="Se usa cuando el producto no tiene historia de despachos"
                      persistent-hint
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      label="Stock máximo por defecto*"
                      v-model="item.stock_max_default"
                      :disabled="disabled"
                      type="number"
                      min="1"
                      :rules="[rules.required]"
                      hint="Se usa cuando el producto no tiene historia de despachos"
                      persistent-hint
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field label="Factor de llenado" v-model="item.factor_llenado" :disabled="disabled" type="number" min="0" max="1" step="0.05">
                      <template v-slot:append-inner>
                        <v-tooltip location="top" max-width="350">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="small" color="grey">mdi-information-outline</v-icon>
                          </template>
                          <span
                            >Proporción del volumen de la ubicación que se considera aprovechable (entre 0 y 1, por defecto 0,85). Se usa para calcular la capacidad de cada ubicación y cuántas asignar
                            para cubrir el máximo.</span
                          >
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field label="Umbral de cambio (%)" v-model="item.umbral_cambio_pct" :disabled="disabled" type="number" min="0" step="1">
                      <template v-slot:append-inner>
                        <v-tooltip location="top" max-width="350">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="small" color="grey">mdi-information-outline</v-icon>
                          </template>
                          <span
                            >Histéresis: la regla solo se actualiza si el nuevo mínimo o máximo difiere del actual en más de este porcentaje (por defecto 15%). Evita que las reglas cambien por
                            variaciones chicas de la demanda.</span
                          >
                        </v-tooltip>
                      </template>
                    </v-text-field>
                  </v-col>

                  <v-col cols="12" v-if="max_days_menor_a_min">
                    <v-alert type="warning" density="compact" variant="tonal">La cobertura del máximo debe ser mayor a la del mínimo.</v-alert>
                  </v-col>
                  <v-col cols="12" v-if="max_default_menor_a_min">
                    <v-alert type="warning" density="compact" variant="tonal">El stock máximo por defecto debe ser mayor al stock mínimo por defecto.</v-alert>
                  </v-col>
                  <v-col cols="12" v-if="factor_invalido">
                    <v-alert type="warning" density="compact" variant="tonal">El factor de llenado debe estar entre 0 y 1.</v-alert>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-switch v-model="item.redondear_a_caja" color="primary" :disabled="disabled" hide-details>
                      <template v-slot:label>
                        Redondear a caja
                        <v-tooltip location="top" max-width="380">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="small" color="grey" class="ml-1">mdi-information-outline</v-icon>
                          </template>
                          <span>
                            Si está activo, el mínimo y el máximo calculados por demanda se redondean hacia arriba a cajas completas (si el producto tiene caja). Ej: 2,1 u./día × 2 días = 5 u.; con
                            caja de 12 queda en 12. Con demanda baja respecto de la caja conviene desactivarlo. No se aplica a los valores por defecto.
                          </span>
                        </v-tooltip>
                      </template>
                    </v-switch>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-switch v-model="item.activo" label="Activa" color="green" :disabled="disabled" hide-details></v-switch>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="error" text @click="$emit('updateDialog', false)">Cerrar</v-btn>
            <v-btn color="success" text type="submit" :loading="loading" v-if="!disabled">{{ formBtn }}</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import ValidMixin from "@/mixins/ValidMixin.vue";
import { almacenService } from "@/libs/ws/almacenService";
import { clientService } from "@/libs/ws/clientService";
import { rubroService } from "@/libs/ws/rubroService";
import { subrubroService } from "@/libs/ws/subrubroService";
import { tipoUbicacionService } from "@/libs/ws/tipoUbicacionService";
import UserTypes from "@/constants/user_type";

export default {
  mixins: [ValidMixin],
  props: {
    service: Object,
    button_name: String,
    modal_title: String,
    page_title: String,
    formBtn: String,
    dialog: Boolean,
    updateDialog: Function,
    loadList: Function,
    editItem: Object,
  },
  data() {
    return {
      disabled: false,
      loading: false,
      loading_tipos: false,
      almacenes: [],
      clients: [],
      categories: [],
      subcategories: [],
      tipos_ubicacion: [],
      item: {
        id: "",
        almacen: null,
        client: null,
        category: null,
        subcategory: null,
        tipo_ubicacion: null,
        min_days: null,
        max_days: null,
        back_period_days: 180,
        stock_min_default: null,
        stock_max_default: null,
        factor_llenado: 0.85,
        umbral_cambio_pct: 15,
        redondear_a_caja: false,
        activo: true,
      },
      defaultItem: null,
    };
  },
  computed: {
    //Mismo criterio que PoliticaReposicion::GetObservacion() en el backend
    tipo_medidas_faltantes() {
      const tipo = this.item.tipo_ubicacion;
      if (tipo == null || typeof tipo !== "object") return [];
      const faltantes = [];
      if (!(parseFloat(tipo.high) > 0)) faltantes.push("alto");
      if (!(parseFloat(tipo.length) > 0)) faltantes.push("largo");
      if (!(parseFloat(tipo.width) > 0)) faltantes.push("ancho");
      return faltantes;
    },
    max_days_menor_a_min() {
      if (this.isEmpty(this.item.min_days) || this.isEmpty(this.item.max_days)) return false;
      return parseFloat(this.item.max_days) <= parseFloat(this.item.min_days);
    },
    max_default_menor_a_min() {
      if (this.isEmpty(this.item.stock_min_default) || this.isEmpty(this.item.stock_max_default)) return false;
      return parseInt(this.item.stock_max_default) <= parseInt(this.item.stock_min_default);
    },
    factor_invalido() {
      if (this.isEmpty(this.item.factor_llenado)) return false;
      const f = parseFloat(this.item.factor_llenado);
      return isNaN(f) || f <= 0 || f > 1;
    },
  },
  watch: {
    dialog() {
      if (this.dialog) {
        this.disabled = this.formBtn == "";

        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }

        if (this.editItem != null && this.editItem.id > 0) {
          this.item = JSON.parse(JSON.stringify(this.editItem));
        } else if (this.defaultItem != null) {
          this.item = JSON.parse(JSON.stringify(this.defaultItem));
        }

        this.subcategories = [];
        this.tipos_ubicacion = [];
        if (this.item.category != null && this.item.category.id > 0) this.loadSubcategories(this.item.category.id);
        if (this.item.almacen != null) this.loadTiposUbicacion(this.item.almacen);
      }
    },
  },
  mounted() {
    this.loadAlmacenes();
    this.loadCategories();
    if (!this.isClient()) this.loadClients();

    this.defaultItem = JSON.parse(JSON.stringify(this.item));
  },
  methods: {
    isClient() {
      return localStorage.getItem("user_type") == UserTypes.CLIENT;
    },
    isEmpty(value) {
      return value === null || value === undefined || value === "";
    },
    loadAlmacenes() {
      almacenService.filter({}).then((response) => {
        this.almacenes = response.result.list;
        //Alta: se preselecciona el unico almacen disponible o, si hay varios, el almacen por
        //defecto del usuario (lo guarda el login), siempre que este entre los habilitados. No pisa
        //una seleccion ya hecha. Se usa el objeto de la lista: trae la sucursal para los tipos.
        const preseleccionado = this.almacenes.length === 1 ? this.almacenes[0] : this.almacenPorDefecto();
        if (preseleccionado != null) {
          if (this.defaultItem != null && this.defaultItem.almacen == null) this.defaultItem.almacen = preseleccionado;
          if (this.item.almacen == null && !(this.item.id > 0)) {
            this.item.almacen = preseleccionado;
            this.loadTiposUbicacion(preseleccionado);
          }
        }
      });
    },
    almacenPorDefecto() {
      let almacen = null;
      try {
        almacen = JSON.parse(localStorage.getItem("almacen"));
      } catch (e) {
        return null;
      }
      if (almacen == null || !almacen.id) return null;
      return this.almacenes.find((a) => a.id == almacen.id) || null;
    },
    loadClients() {
      clientService
        .filter({})
        .then((response) => {
          this.clients = response.result.list;
        })
        .catch((e) => {
          console.log("clientService.filter error", e);
        });
    },
    loadCategories() {
      rubroService
        .filter({})
        .then((response) => {
          this.categories = response.result.list;
        })
        .catch((e) => {
          console.log("rubroService.filter error", e);
        });
    },
    loadSubcategories(category_id) {
      subrubroService
        .filter({ category: category_id })
        .then((response) => {
          this.subcategories = response.result.list;
        })
        .catch((e) => {
          console.log("subrubroService.filter error", e);
        });
    },
    loadTiposUbicacion(almacen) {
      //TipoUbicacion es por sucursal: solo sirven los de la sucursal del almacen y de picking
      if (almacen == null || almacen.sucursal == null) {
        this.tipos_ubicacion = [];
        return;
      }
      this.loading_tipos = true;
      tipoUbicacionService
        .filter({ sucursal: { id: almacen.sucursal.id } })
        .then((response) => {
          this.tipos_ubicacion = response.result.list.filter((t) => t.allow_picking);
        })
        .catch((e) => {
          this.tipos_ubicacion = [];
          console.log("tipoUbicacionService.filter error", e);
        })
        .finally(() => {
          this.loading_tipos = false;
        });
    },
    onAlmacenChange(almacen) {
      //Cambiar de almacen puede cambiar de sucursal: el tipo de ubicacion elegido deja de servir
      this.item.tipo_ubicacion = null;
      this.tipos_ubicacion = [];
      if (almacen != null && typeof almacen === "object") this.loadTiposUbicacion(almacen);
    },
    onCategoryChange(category) {
      //La subcategoria tiene que pertenecer a la categoria
      this.item.subcategory = null;
      this.subcategories = [];
      if (category != null && category.id > 0) this.loadSubcategories(category.id);
    },
    async createItem() {
      if (this.loading) return;

      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        this.toast.error("Por favor complete todos los campos obligatorios");
        return;
      }

      if (this.max_days_menor_a_min) {
        this.toast.error("La cobertura del máximo debe ser mayor a la del mínimo.");
        return;
      }
      if (this.max_default_menor_a_min) {
        this.toast.error("El stock máximo por defecto debe ser mayor al stock mínimo por defecto.");
        return;
      }
      if (this.factor_invalido) {
        this.toast.error("El factor de llenado debe estar entre 0 y 1.");
        return;
      }

      var client = null;
      if (this.isClient()) {
        //El usuario cliente solo puede cargar politicas propias: el backend lo exige
        const app_object = JSON.parse(localStorage.getItem("app_object"));
        client = app_object && app_object.id ? { id: app_object.id } : null;
      } else if (this.item.client != null && this.item.client.id > 0) {
        client = { id: this.item.client.id };
      }

      var data = {
        id: this.item.id,
        almacen: { id: this.item.almacen.id },
        client: client,
        category: { id: this.item.category.id },
        subcategory: this.item.subcategory != null && this.item.subcategory.id > 0 ? { id: this.item.subcategory.id } : null,
        tipo_ubicacion: { id: this.item.tipo_ubicacion.id },
        min_days: parseFloat(this.item.min_days),
        max_days: parseFloat(this.item.max_days),
        back_period_days: this.isEmpty(this.item.back_period_days) ? null : parseInt(this.item.back_period_days),
        stock_min_default: parseInt(this.item.stock_min_default),
        stock_max_default: parseInt(this.item.stock_max_default),
        factor_llenado: this.isEmpty(this.item.factor_llenado) ? null : parseFloat(this.item.factor_llenado),
        umbral_cambio_pct: this.isEmpty(this.item.umbral_cambio_pct) ? null : parseFloat(this.item.umbral_cambio_pct),
        //Solo redondea si se activo explicitamente: el default es NO
        redondear_a_caja: this.item.redondear_a_caja === true,
        activo: this.item.activo,
      };

      this.loading = true;
      const request = this.item.id > 0 ? this.service.update(data) : this.service.create(data);
      const msg_ok = this.item.id > 0 ? "La " + this.modal_title + " se modificó correctamente." : "La " + this.modal_title + " se creó correctamente";

      request
        .then((response) => {
          if (response.status) {
            this.toast.success(msg_ok);
            this.$emit("updateDialog", false);
            this.$emit("loadList");
          } else {
            if (Object.prototype.hasOwnProperty.call(response, "message")) {
              this.toast.error(response.message);
            } else {
              this.toast.error(response.msg);
            }
          }
        })
        .catch((e) => {
          this.toast.error(e && e.msg ? e.msg : e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
